import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CuttysarkDivision,
  CuttysarkLiveCandidate,
} from '@cortex/domain-product-innovation-lab-types';
import { candidatePhoto } from '../cuttysark-helpers.js';

/** 写真の props @graph-connects none */
interface StagePhotoProps {
  candidate: CuttysarkLiveCandidate;
  /** 大きさ（Tailwind のサイズクラス）。 */
  sizeClass: string;
  /** 追加のクラス（出現アニメーション等）。 */
  className?: string;
}

/**
 * 候補者の写真（無ければ頭文字）。金の縁取りで丸く出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の候補者写真（無ければ頭文字）
 * @graph-connects candidatePhoto [calls] 表示する写真の決定
 */
export function StagePhoto({ candidate, sizeClass, className = '' }: StagePhotoProps): ReactNode {
  const src = candidatePhoto(candidate);
  const frame = `${sizeClass} shrink-0 rounded-full border-4 border-amber-300 shadow-[0_0_40px_rgb(250_204_21/0.45)] ${className}`;
  if (src === '') {
    return (
      <div
        className={`${frame} flex items-center justify-center bg-blue-900 text-[clamp(2rem,6vw,5rem)] font-black text-amber-200`}
      >
        {candidate.name.slice(0, 1)}
      </div>
    );
  }
  return <img src={src} alt={candidate.name} className={`${frame} object-cover`} />;
}

/** スポットライトの props @graph-connects none */
interface CandidateSpotlightProps {
  candidate: CuttysarkLiveCandidate;
  divisionName: string;
  /** 上に出す帯（「発表中」など）。無ければ出さない。 */
  banner?: string;
}

/**
 * 1 人を大きく紹介する（代表者紹介・発表中）。写真がズームで現れ、二つ名が金色で打ち出され、
 * 名前が大きく出る。呼び出し側がメールを key にするので、人が替わるたびにアニメーションがやり直される。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の代表者紹介（写真のズーム・二つ名の打ち出し・名前・紹介文）
 * @graph-connects StagePhoto [calls] 候補者写真
 */
export function CandidateSpotlight({
  candidate,
  divisionName,
  banner,
}: CandidateSpotlightProps): ReactNode {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-[3vh] px-[6vw] text-center">
      {banner !== undefined && (
        <div className="cs-pulse rounded-full bg-red-600 px-6 py-1 text-[clamp(1rem,2vw,1.6rem)] font-bold tracking-widest text-white">
          {banner}
        </div>
      )}
      <StagePhoto candidate={candidate} sizeClass="h-[34vh] w-[34vh]" className="cs-zoom-in" />
      {candidate.title !== '' && (
        <div className="cs-type text-[clamp(1.6rem,4.2vw,3.6rem)] font-bold text-amber-300 [animation-delay:0.9s] [text-shadow:0_0_24px_rgb(250_204_21/0.6)]">
          {candidate.title}
        </div>
      )}
      <div className="cs-slide-up text-[clamp(2.8rem,8vw,7rem)] font-black leading-none text-white [animation-delay:1.8s]">
        {candidate.name}
      </div>
      <div className="cs-slide-up text-[clamp(1rem,2vw,1.6rem)] tracking-[0.3em] text-blue-200 [animation-delay:2.2s]">
        {divisionName}
      </div>
      {candidate.intro !== '' && (
        <p className="cs-slide-up max-w-[70vw] text-[clamp(1rem,2vw,1.6rem)] leading-relaxed text-blue-100 [animation-delay:2.6s]">
          {candidate.intro}
        </p>
      )}
    </div>
  );
}

/** 一覧の props @graph-connects none */
interface CandidateGridProps {
  divisions: CuttysarkDivision[];
  candidates: CuttysarkLiveCandidate[];
}

/**
 * 部門ごとに候補者を並べる（紹介前・発表の合間）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面の候補者一覧（部門ごと）
 * @graph-connects StagePhoto [calls] 候補者写真
 */
export function CandidateGrid({ divisions, candidates }: CandidateGridProps): ReactNode {
  const { t } = useTranslation();
  if (candidates.length === 0) {
    return (
      <p className="text-[clamp(1.4rem,3vw,2.4rem)] text-blue-200">
        {t('cuttysark.stageScreen.noCandidates')}
      </p>
    );
  }
  return (
    <div className="flex w-full flex-col gap-[4vh] px-[5vw]">
      {divisions.map((division) => {
        const members = candidates.filter((c) => c.divisionId === division.id);
        if (members.length === 0) return null;
        return (
          <section key={division.id}>
            <h2 className="mb-[2vh] text-[clamp(1.2rem,2.4vw,2rem)] font-bold tracking-[0.3em] text-amber-300">
              {division.name}
            </h2>
            <div className="flex flex-wrap justify-center gap-[3vw]">
              {members.map((c, i) => (
                <div
                  key={c.email}
                  className="cs-pop flex w-[14vw] min-w-28 flex-col items-center gap-2 text-center"
                  style={{ animationDelay: `${String(i * 120)}ms` }}
                >
                  <StagePhoto candidate={c} sizeClass="h-[12vw] w-[12vw] min-h-24 min-w-24" />
                  {c.title !== '' && (
                    <div className="text-[clamp(0.8rem,1.3vw,1.2rem)] font-semibold text-amber-200">
                      {c.title}
                    </div>
                  )}
                  <div className="text-[clamp(1.1rem,2vw,1.8rem)] font-bold text-white">
                    {c.name}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
